import React, { useState } from "react";
import {
    PendingCell,
    EstimatedTag,
    SortableTh,
    TableCard,
    fmtMoney2,
    theadStyle,
    trNum,
} from "./PosReportShared";

/** Tıklanabilir satırın üzerine gelindiğindeki gri vurgu (projedeki açık zemin tonu). */
const HOVER_BG = "#f6f7fb";

/**
 * Ad/unvan kolonlarının varsayılan genişliği. Uzun cari unvanları ("Öküz Karası
 * Şarapçılık A.Ş." gibi) tabloyu ölçüsüz genişletiyordu; kolon sabit kalıp taşan
 * kısım "…" ile kısaltılır, tam metin tooltip'te durur. Kolon tanımında
 * {@code width} verilerek değiştirilebilir.
 */
const NAME_COLUMN_WIDTH = 220;

const cellStyle = (column) => (
    column.width === null ? undefined : {
        maxWidth: column.width || NAME_COLUMN_WIDTH,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }
);

/* ============================================================================
 * POS Rapor hiyerarşisinin ortak ölçüm tablosu.
 *
 * Sağlayıcı / firma / cari seviyelerinde kolon seti aynıdır; yalnızca soldaki
 * kimlik kolonları değişir (`identityColumns`). Böylece üç ekran tek bileşenle
 * ve tek kolon tanımıyla beslenir.
 *
 * SUPER_ADMIN'e özel kolonlar (Debit Oranı, Credit Oranı, Komisyon Kazancı)
 * `showAdminColumns` ile açılır. Bu kolonların verisi henüz toplanmadığı için
 * "—" gösterilir — sahte değer üretilmez.
 * ========================================================================== */

/**
 * Oran + tutar birlikte: "%30 (60.000,00 ₺)". Oran tam sayı değilse iki hane
 * gösterilir — yuvarlarsak satırdaki debit/credit payları %100'ü tutmayabilir.
 */
const RateWithAmount = ({ rate, amount }) => {
    if (rate == null) {
        return <PendingCell hint="Bu dönemde kart tipi bilinen işlem yok (BIN kaydı öncesi ödemeler)" />;
    }
    const value = Number(rate);
    return (
        <span>
            %{Number.isInteger(value) ? value : value.toFixed(2)}
            <span className="text-muted"> ({fmtMoney2(amount)})</span>
        </span>
    );
};

export default function PosScopeTable({
    rows,
    totals,
    identityColumns,      // [{ key, label, render(row), className, sortKey }]
    showAdminColumns,
    onRowClick,           // (row) => void | null
    isRowClickable,       // (row) => boolean
    emptyText = "Bu dönemde kayıt bulunmuyor.",
    extraColumns = [],    // kimlik kolonlarından sonra, ölçümlerden önce
    footer,
    // Kolon başlığı sıralaması; onSort verilmezse başlıklar tıklanamaz olur.
    sort,
    direction,
    onSort,
}) {
    const [hoveredKey, setHoveredKey] = useState(null);

    // Başlıklar tek yerden çizilir; sıralama durumu her birine aynı şekilde geçer.
    const Th = (props) => <SortableTh {...props} sort={sort} direction={direction} onSort={onSort} />;

    const clickable = (r) => (isRowClickable ? isRowClickable(r) : !!onRowClick);
    // Tıklanabilir tablolarda satır sonuna ">" kolonu eklenir (kök tablodaki desen).
    const hasChevron = !!onRowClick;
    const colCount =
        identityColumns.length + extraColumns.length + 7 +
        (showAdminColumns ? 3 : 0) + (hasChevron ? 1 : 0);

    return (
        <TableCard>
            {/* Bootstrap'in table-hover'ı ayrım yapmadan tüm satırları vurgulardı;
                vurgu yalnızca gerçekten tıklanabilir satırlara uygulanır. */}
            <table className="table align-middle mb-0">
                <thead>
                    <tr style={theadStyle}>
                        {[...identityColumns, ...extraColumns].map((c) => (
                            <Th key={c.key} sortKey={c.sortKey}>{c.label}</Th>
                        ))}
                        <Th sortKey="transactionCount" className="text-center">İşlem Sayısı</Th>
                        <Th sortKey="avgAmount" className="text-center">Ortalama Tutar</Th>
                        <Th sortKey="grossAmount" className="text-center">İşlem Tutarı</Th>
                        <Th sortKey="commissionAmount" className="text-center">Komisyon Tutarı</Th>
                        <Th sortKey="refundedAmount" className="text-center">İptal / İade Toplamı</Th>
                        <Th sortKey="netAmount" className="text-center">Net Tutar</Th>
                        <Th sortKey="valorAmount" className="text-center">Valör Toplamı</Th>
                        {showAdminColumns && (
                            <>
                                <Th sortKey="debitRate" className="text-center">Debit Oranı</Th>
                                <Th sortKey="creditRate" className="text-center">Credit Oranı</Th>
                                <Th sortKey="commissionEarning" className="text-center">Komisyon Kazancı</Th>
                            </>
                        )}
                        {hasChevron && <th className="border-0" style={{ width: 32 }} />}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={colCount} className="text-center text-muted py-4">{emptyText}</td>
                        </tr>
                    ) : (
                        rows.map((r, i) => {
                            const canClick = clickable(r);
                            const rowKey = r.accountId || r.companyId || r.provider || i;
                            return (
                                <tr
                                    key={rowKey}
                                    style={{
                                        borderTop: "1px solid #f0f2f6",
                                        ...(canClick ? { cursor: "pointer" } : null),
                                        ...(canClick && hoveredKey === rowKey ? { background: HOVER_BG } : null),
                                    }}
                                    onClick={canClick ? () => onRowClick(r) : undefined}
                                    onMouseEnter={canClick ? () => setHoveredKey(rowKey) : undefined}
                                    onMouseLeave={canClick ? () => setHoveredKey(null) : undefined}
                                >
                                    {[...identityColumns, ...extraColumns].map((c) => {
                                        const content = c.render(r);
                                        return (
                                            <td
                                                key={c.key}
                                                className={c.className}
                                                style={cellStyle(c)}
                                                // Kısaltılan adın tamamı tooltip'te görünsün
                                                title={typeof content === "string" ? content : undefined}
                                            >
                                                {content}
                                            </td>
                                        );
                                    })}
                                    <td className="text-center">{trNum(r.transactionCount)}</td>
                                    <td className="text-center">{fmtMoney2(r.avgAmount)}</td>
                                    <td className="text-center fw-bold text-dark">{fmtMoney2(r.grossAmount)}</td>
                                    <td className="text-center">
                                        {fmtMoney2(r.commissionAmount)}
                                        {r.commissionEstimatedCount > 0 && <EstimatedTag count={r.commissionEstimatedCount} />}
                                    </td>
                                    <td className="text-center">{fmtMoney2(r.refundedAmount)}</td>
                                    <td className="text-center fw-semibold">{fmtMoney2(r.netAmount)}</td>
                                    <td className="text-center">{fmtMoney2(r.valorAmount)}</td>
                                    {showAdminColumns && (
                                        <>
                                            <td className="text-center">
                                                <RateWithAmount rate={r.debitRate} amount={r.debitAmount} />
                                            </td>
                                            <td className="text-center">
                                                <RateWithAmount rate={r.creditRate} amount={r.creditAmount} />
                                            </td>
                                            <td className="text-center">
                                                {r.commissionEarning == null ? (
                                                    <PendingCell hint="Sağlayıcı maliyet oranı sistemde tutulmuyor" />
                                                ) : (
                                                    <span>
                                                        {fmtMoney2(r.commissionEarning)}
                                                        {r.commissionEarningRate != null && (
                                                            <span className="text-muted">
                                                                {" "}(%{Number(r.commissionEarningRate).toFixed(2)})
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </td>
                                        </>
                                    )}
                                    {hasChevron && (
                                        <td className="text-end">
                                            {canClick && (
                                                <i className="bx bx-chevron-right" style={{ color: "#8a93a6" }} />
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })
                    )}
                </tbody>
                {totals && rows.length > 0 && (
                    <tfoot>
                        <tr className="fw-bold" style={{ borderTop: "2px solid #0A316E" }}>
                            <td>TOPLAM</td>
                            {[...identityColumns.slice(1), ...extraColumns].map((c) => (
                                <td key={c.key} />
                            ))}
                            <td className="text-center">{trNum(totals.transactionCount)}</td>
                            <td className="text-center">{fmtMoney2(totals.avgAmount)}</td>
                            <td className="text-center">{fmtMoney2(totals.grossAmount)}</td>
                            <td className="text-center">
                                {fmtMoney2(totals.commissionAmount)}
                                {totals.commissionEstimatedCount > 0 && <EstimatedTag count={totals.commissionEstimatedCount} />}
                            </td>
                            <td className="text-center">{fmtMoney2(totals.refundedAmount)}</td>
                            <td className="text-center">{fmtMoney2(totals.netAmount)}</td>
                            <td className="text-center">{fmtMoney2(totals.valorAmount)}</td>
                            {showAdminColumns && (
                                <>
                                    {/* Debit/Credit oranları toplam satırında anlamlı bir tek değere
                                        indirgenemez (kart tipi bilinmeyen işlemler payda dışında kalır). */}
                                    <td />
                                    <td />
                                    {/* Kazanç yalnız oranı tanımlı satırlardan toplanır; hiçbiri
                                        tanımlı değilse backend alanı null bırakır → "—". */}
                                    <td className="text-center">
                                        {totals.commissionEarning == null ? (
                                            <PendingCell hint="Kapsamdaki hiçbir firma için komisyon hakediş oranı tanımlı değil" />
                                        ) : (
                                            fmtMoney2(totals.commissionEarning)
                                        )}
                                    </td>
                                </>
                            )}
                            {hasChevron && <td />}
                        </tr>
                    </tfoot>
                )}
            </table>
            {footer}
        </TableCard>
    );
}
